import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  fetchJobsListWithBerthData,
  fetchJobsListWithBerthDataStatic,
  fetchVoyagesList,
  getVoyagesList,
} from "../thunks/voyagesThunks";

import type { Job, JobList, Voyage } from "../../utils/types/voyages";
import type { PayloadAction } from "@reduxjs/toolkit";

interface VoyageState {
  voyageList: Voyage[];
  voyageListData: JobList[];
  showMyVoyages: boolean;
  selectedVoyage: Voyage | null;
  loading: boolean;
  error: string | null;
  total: number;
  jobListData: Job[]; // updated datas array for voyages component
  jobListStatic: Job[];
  staticJobListLoading: boolean;
  staticJobListError: string | null;
  newJobListStaticAvailable: boolean;
  newJobsAvailable: boolean;
  historicalJobListData: Job[]; // updated datas array for voyages component
  historicalTotal?: number;

  // Query Key tracker to ignore stale responses for the active Voyages view
  currentVoyagesQueryKey: string | null;
}

const initialState: VoyageState = {
  voyageList: [],
  voyageListData: [],
  selectedVoyage: null,
  showMyVoyages: false,
  loading: false,
  error: null,
  total: 0,
  jobListData: [],
  jobListStatic: [],
  newJobsAvailable: false,
  newJobListStaticAvailable: false,
  historicalJobListData: [],
  historicalTotal: 0,
  staticJobListLoading: false,
  staticJobListError: null,
  currentVoyagesQueryKey: null,
};

export const getQueryKey = (arg: any) => {
  if (!arg) return "";
  const { page, pageSize, version, statusValue, q, sort, agentId, myVoyagesOnly } = arg;
  return JSON.stringify({
    page,
    pageSize,
    version,
    statusValue: statusValue || "",
    q: q || "",
    sort: sort || null,
    agentId: agentId || null,
    myVoyagesOnly: !!myVoyagesOnly,
  });
};

const voyages = createSlice({
  name: "voyages",
  initialState,
  reducers: {
    selectVoyage(state, action: PayloadAction<Voyage>) {
      state.selectedVoyage = action.payload;
    },
    clearSelectedVoyage(state) {
      state.selectedVoyage = null;
    },
    toggleShowMyVoyages(state) {
      state.showMyVoyages = !state.showMyVoyages;
    },
    resetShowMyVoyages(state) {
      state.showMyVoyages = false;
    },
    toogleNewJobsAvailable(state, action: PayloadAction<boolean>) {
      state.newJobsAvailable = action.payload;
    },
    toogleNewJobStaticAvailable(state, action: PayloadAction<boolean>) {
      state.newJobListStaticAvailable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVoyagesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchVoyagesList.fulfilled,
        (
          state,
          action: PayloadAction<{
            voyages: Voyage[];
            total: number;
            version: string;
          }>
        ) => {
          state.loading = false;
          state.voyageList = action.payload?.voyages || [];
          state.total = action.payload.total;
        }
      )
      .addCase(fetchVoyagesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getVoyagesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getVoyagesList.fulfilled,
        (
          state,
          action: PayloadAction<{ voyages: JobList[]; total: number }>
        ) => {
          state.loading = false;
          state.voyageListData = action.payload?.voyages || [];
          state.total = action.payload.total;
        }
      )
      .addCase(getVoyagesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Updated functions
    builder
      .addCase(fetchJobsListWithBerthData.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentVoyagesQueryKey = getQueryKey(action.meta.arg);
      })
      .addCase(
        fetchJobsListWithBerthData.fulfilled,
        (
          state,
          action
        ) => {
          const queryKey = getQueryKey(action.meta.arg);
          if (state.currentVoyagesQueryKey && state.currentVoyagesQueryKey !== queryKey) {
            return;
          }
          state.loading = false;
          state.newJobsAvailable = false;
          if (action.payload?.version === "historical") {
            state.historicalJobListData = action.payload?.jobs || [];
            state.historicalTotal = action.payload.total;
          } else {
            state.jobListData = action.payload?.jobs || [];
            state.total = action.payload.total;
            if (!action.payload?.filterActive) {
              state.newJobListStaticAvailable = false;
              state.jobListStatic = action.payload?.jobs || [];
            }
          }
        }
      )
      .addCase(fetchJobsListWithBerthData.rejected, (state, action) => {
        const queryKey = getQueryKey(action.meta.arg);
        if (state.currentVoyagesQueryKey && state.currentVoyagesQueryKey !== queryKey) {
          return;
        }
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchJobsListWithBerthDataStatic.pending, (state) => {
        state.staticJobListLoading = true;
      })
      .addCase(
        fetchJobsListWithBerthDataStatic.fulfilled,
        (
          state,
          action: PayloadAction<{
            jobs: Job[];
            total: number;
          }>
        ) => {
          state.newJobListStaticAvailable = false;
          state.jobListStatic = action.payload?.jobs || [];
          state.staticJobListLoading = false;
        }
      )
      .addCase(fetchJobsListWithBerthDataStatic.rejected, (state, action) => {
        state.staticJobListLoading = false;
        state.staticJobListError = action.payload as string;
      });

    addWebSocketCases(builder, "voyages", "jobListStatic");
  },
});

export default voyages.reducer;

export const {
  selectVoyage,
  clearSelectedVoyage,
  toggleShowMyVoyages,
  resetShowMyVoyages,
  toogleNewJobStaticAvailable,
  toogleNewJobsAvailable,
} = voyages.actions;
